/**
 * Adaptive downsampling — the budget arithmetic.
 *
 * Item 3 sized the per-request point budget from ONE input, the chart's pixel
 * width. This module adds two more — connection throughput and browser memory —
 * and resolves all three plus a manual override into a single number, which the
 * controller injects where `resolutionForWidth` used to be read.
 *
 * The budget is a MINIMUM of ceilings, never a weighted blend:
 *
 *     budget = quantise( min(pixelCeiling, throughputAllowance, memoryAllowance) )
 *
 * Each input answers a different question and each is independently a reason not
 * to fetch more: can the screen show it, will it arrive in time, will holding it
 * survive. Averaging them would let a fast connection talk us past the memory
 * ceiling, which is the failure mode that kills the tab.
 *
 * Everything here is pure and browser-free. Feature detection, EWMA state and
 * ring-buffer enforcement live in adaptive-resolution.service.
 */

import { MAX_RESOLUTION, MIN_RESOLUTION } from './telemetry-downsample';

/** Manual override. `auto` is the computed minimum; the rest are user caps. */
export type QualityMode = 'auto' | 'low' | 'medium' | 'high' | 'max';

export const QUALITY_MODES: readonly QualityMode[] = [
  'auto',
  'low',
  'medium',
  'high',
  'max',
];

/**
 * Budgets snap to these rungs.
 *
 * Quantising is what stops ABR thrash: an unquantised budget drifts by a few
 * points every sample, and since the budget is part of the request URL, every
 * drift is a fresh full-window refetch at a near-identical resolution. The user
 * sees flicker; the network sees pointless load.
 */
export const RESOLUTION_LADDER: readonly number[] = [
  256, 512, 1024, 2048, 4096, 8192,
];

/** How long a new rung must hold before it takes effect (upward moves only). */
export const DWELL_MS = 10_000;

/** Target wall-clock for one telemetry response, before RTT is subtracted. */
export const TARGET_RESPONSE_MS = 1500;

/**
 * EWMA smoothing. 0.3 tracks a real network change in ~3 samples (~15 s at the
 * 5 s poll cadence) while a single stalled response moves the estimate less than
 * a third of the way.
 */
export const EWMA_ALPHA = 0.3;

/** JSON bytes per transmitted point, before the first real measurement. */
export const BYTES_PER_POINT_ESTIMATE = 9;

/** Heap bytes per retained point — one JS number array element. */
export const BYTES_PER_POINT_HEAP = 8;

/** Rows per response, before the first real measurement. */
export const DEFAULT_ROWS_PER_RESPONSE = 20;

/** Assumed device memory (GiB) when navigator.deviceMemory is absent. */
export const DEFAULT_DEVICE_MEMORY_GIB = 4;

/**
 * Assumed memory for a platform that reports none — Safari and iOS.
 *
 * 1 GiB, not 4: an iPad over LTE is the constrained case this whole budget
 * exists to protect, and assuming a desktop's headroom there is the one error
 * that cannot be recovered from once the tab is killed.
 */
export const CONSTRAINED_DEVICE_MEMORY_GIB = 1;

/** Share of device memory this tab may spend on retained telemetry. */
export const HEAP_SAFETY_FRACTION = 0.25;

/**
 * Hard ceiling on total buffered points across every registered ring buffer.
 *
 * 50 M points x 8 B = 400 MB. The measured worst case today is ~316 MB (3 buffers
 * x 600 ticks x 22,000 points), so this sits just above the known-good bound and
 * catches any future retention or family added without re-doing the sizing math.
 */
export const MAX_BUFFERED_POINTS = 50_000_000;

/** Retention floor — eviction never shrinks a buffer below this many ticks. */
export const MIN_TICK_RETENTION = 60;

/** Heap-pressure thresholds (usedJSHeapSize / jsHeapSizeLimit). */
export const PRESSURE_MILD = 0.6;
export const PRESSURE_HIGH = 0.75;
export const PRESSURE_EVICT = 0.85;

/** What heap pressure demands of the budget. */
export type PressureAction = 'none' | 'halve' | 'floor' | 'evict';

/** Network signals, already feature-detected. */
export interface NetworkSignals {
  /** Mbps from navigator.connection.downlink, or null. */
  downlinkMbps: number | null;
  /** navigator.connection.effectiveType, or null. */
  effectiveType: string | null;
  /** navigator.connection.rtt in ms, or null. */
  rttMs: number | null;
}

/** Memory signals, already feature-detected. */
export interface MemorySignals {
  /** navigator.deviceMemory in GiB, or null. */
  deviceMemoryGiB: number | null;
  /** performance.memory.usedJSHeapSize, or null. */
  usedHeapBytes: number | null;
  /** performance.memory.jsHeapSizeLimit, or null. */
  heapLimitBytes: number | null;
}

/** Mbps to bytes per millisecond. */
export function mbpsToBytesPerMs(mbps: number): number {
  return (mbps * 1_000_000) / 8 / 1000;
}

/**
 * Goodput floor implied by effectiveType, in bytes/ms.
 *
 * Used when `downlink` is missing and as a sanity floor when it is present but
 * implausibly small. Values are the nominal rates the spec buckets describe.
 */
export function effectiveTypeBytesPerMs(effectiveType: string | null): number | null {
  switch (effectiveType) {
    case 'slow-2g':
      return mbpsToBytesPerMs(0.05);
    case '2g':
      return mbpsToBytesPerMs(0.25);
    case '3g':
      return mbpsToBytesPerMs(1.5);
    case '4g':
      return mbpsToBytesPerMs(10);
    default:
      return null;
  }
}

/**
 * Initial goodput estimate, in bytes/ms.
 *
 * Seeds NEUTRAL (4g-ish) when the browser ships no NetworkInformation at all —
 * Safari and Firefox never do. Being wrong-fast for one request self-corrects on
 * the next sample; being wrong-slow would make the first paint needlessly coarse
 * for every non-Chromium user, permanently, since a coarse first request also
 * produces a small sample that keeps the estimate low.
 */
export function seedGoodputBytesPerMs(signals: NetworkSignals): number {
  const fromType = effectiveTypeBytesPerMs(signals.effectiveType);
  const fromDownlink =
    typeof signals.downlinkMbps === 'number' && signals.downlinkMbps > 0
      ? mbpsToBytesPerMs(signals.downlinkMbps)
      : null;

  if (fromDownlink !== null && fromType !== null) {
    // downlink is the sharper number; effectiveType guards against a stale 0.
    return Math.max(fromDownlink, fromType * 0.5);
  }
  return fromDownlink ?? fromType ?? mbpsToBytesPerMs(10);
}

/** Exponentially weighted moving average; the first sample seeds it outright. */
export function updateEwma(
  previous: number | null,
  sample: number,
  alpha: number = EWMA_ALPHA
): number {
  if (!Number.isFinite(sample) || sample <= 0) {
    return previous ?? 0;
  }
  if (previous === null || !Number.isFinite(previous) || previous <= 0) {
    return sample;
  }
  return alpha * sample + (1 - alpha) * previous;
}

/** Inputs to the throughput term. */
export interface ThroughputInputs {
  goodputBytesPerMs: number;
  rttMs: number | null;
  bytesPerPoint: number;
  rowsPerResponse: number;
}

/**
 * Points affordable within the latency target.
 *
 * RTT is subtracted rather than divided in: round trips are dead time no amount of
 * bandwidth shortens, and on a high-latency link they are most of the budget.
 */
export function throughputAllowance(inputs: ThroughputInputs): number {
  const { goodputBytesPerMs, rttMs, bytesPerPoint, rowsPerResponse } = inputs;
  if (!(goodputBytesPerMs > 0)) {
    return Number.POSITIVE_INFINITY;
  }
  const rtt = typeof rttMs === 'number' && rttMs > 0 ? rttMs : 0;
  const usableMs = Math.max(0, TARGET_RESPONSE_MS - rtt);
  const bytes = goodputBytesPerMs * usableMs;

  const perPoint = bytesPerPoint > 0 ? bytesPerPoint : BYTES_PER_POINT_ESTIMATE;
  const rows = rowsPerResponse > 0 ? rowsPerResponse : DEFAULT_ROWS_PER_RESPONSE;

  return bytes / perPoint / rows;
}

/** Inputs to the memory term. */
export interface MemoryInputs {
  deviceMemoryGiB: number | null;
  /** Total retained rows across every registered ring buffer. */
  retainedRowsTotal: number;
}

/**
 * Points per row affordable in heap.
 *
 * Deliberately static — it depends on device capability and how many rows we hold,
 * not on live heap use. Live pressure is a separate, faster-moving signal
 * (`pressureAction`) so a transient GC spike cannot permanently shrink the budget.
 */
export function memoryAllowance(inputs: MemoryInputs): number {
  const gib =
    typeof inputs.deviceMemoryGiB === 'number' && inputs.deviceMemoryGiB > 0
      ? inputs.deviceMemoryGiB
      : DEFAULT_DEVICE_MEMORY_GIB;
  const rows = inputs.retainedRowsTotal > 0 ? inputs.retainedRowsTotal : 1;

  const budgetBytes = gib * 1024 * 1024 * 1024 * HEAP_SAFETY_FRACTION;
  return budgetBytes / BYTES_PER_POINT_HEAP / rows;
}

/** What live heap pressure demands. Absent measurements mean no action. */
export function pressureAction(signals: MemorySignals): PressureAction {
  const { usedHeapBytes, heapLimitBytes } = signals;
  if (
    typeof usedHeapBytes !== 'number' ||
    typeof heapLimitBytes !== 'number' ||
    !(heapLimitBytes > 0)
  ) {
    return 'none';
  }
  const ratio = usedHeapBytes / heapLimitBytes;
  if (ratio >= PRESSURE_EVICT) return 'evict';
  if (ratio >= PRESSURE_HIGH) return 'floor';
  if (ratio >= PRESSURE_MILD) return 'halve';
  return 'none';
}

/**
 * Upper bound a manual quality mode imposes.
 *
 * `auto` and `max` impose none of their own — `max` differs in that it also drops
 * the throughput term (see combineBudget). Neither escapes the memory ceiling.
 */
export function qualityCap(mode: QualityMode): number {
  switch (mode) {
    case 'low':
      return 512;
    case 'medium':
      return 1024;
    case 'high':
      return 4096;
    case 'auto':
    case 'max':
      return Number.POSITIVE_INFINITY;
  }
}

/** Snap down to a ladder rung, clamped into the item-3 resolution range. */
export function quantiseToLadder(points: number): number {
  if (!Number.isFinite(points) || points <= 0) {
    return MIN_RESOLUTION;
  }
  const clamped = Math.min(MAX_RESOLUTION, Math.max(MIN_RESOLUTION, points));
  let chosen = RESOLUTION_LADDER[0];
  for (const rung of RESOLUTION_LADDER) {
    if (rung <= clamped) {
      chosen = rung;
    }
  }
  return chosen;
}

/** Everything combineBudget needs. */
export interface BudgetInputs {
  pixelCeiling: number;
  throughputAllowance: number;
  memoryAllowance: number;
  quality: QualityMode;
  pressure: PressureAction;
}

/** The three terms plus the override, resolved. Returns a ladder rung. */
export function combineBudget(inputs: BudgetInputs): number {
  const { pixelCeiling, quality, pressure } = inputs;

  // A width of 0 means "not laid out yet" — item 3's signal for full resolution.
  // Preserve it: the caller turns 0 into "send no params".
  if (!(pixelCeiling > 0)) {
    return 0;
  }

  // `max` is the user accepting a slower load, so the throughput term drops out.
  // It is NOT them accepting a dead tab, so the memory term stays.
  const throughput =
    quality === 'max' ? Number.POSITIVE_INFINITY : inputs.throughputAllowance;

  let budget = Math.min(pixelCeiling, throughput, inputs.memoryAllowance);
  budget = Math.min(budget, qualityCap(quality));

  if (pressure === 'halve') {
    budget = budget / 2;
  } else if (pressure === 'floor' || pressure === 'evict') {
    budget = MIN_RESOLUTION;
  }

  return quantiseToLadder(budget);
}

/**
 * Total points a set of buffers is holding.
 *
 * Points, not bytes, because that is the unit the resolution knob moves in; bytes
 * are one multiplication away and only needed for reporting.
 */
export function bufferedPoints(
  buffers: readonly { retainedCount: number; pointsPerRow: number }[]
): number {
  let total = 0;
  for (const buffer of buffers) {
    total += Math.max(0, buffer.retainedCount) * Math.max(0, buffer.pointsPerRow);
  }
  return total;
}

/**
 * Decide how to get back under the hard cap.
 *
 * Resolution first, eviction second, and never below the retention floor:
 * lowering resolution is reversible and touches only future rows, whereas an
 * evicted tick becomes a refetch the moment the user scrubs back to it.
 */
export interface CapVerdict {
  /** True when the cap is exceeded and something must give. */
  overCap: boolean;
  /** Budget to use instead, if lowering resolution alone can fix it. */
  reducedBudget: number | null;
  /**
   * Fraction of its retained rows each buffer should keep, when resolution alone
   * is not enough. A RATIO rather than an absolute retention because the verdict
   * cannot know how many buffers are registered or how wide their rows actually
   * are — the caller applies it per buffer, which is correct for any combination.
   */
  evictRatio: number | null;
}

export function enforceBufferCap(
  currentPoints: number,
  currentBudget: number,
  retainedRowsTotal: number,
  maxPoints: number = MAX_BUFFERED_POINTS
): CapVerdict {
  if (currentPoints <= maxPoints) {
    return { overCap: false, reducedBudget: null, evictRatio: null };
  }

  // What per-row width would fit, if the row count stayed as it is?
  const rows = retainedRowsTotal > 0 ? retainedRowsTotal : 1;
  const affordablePerRow = maxPoints / rows;
  const reduced = quantiseToLadder(affordablePerRow);

  // Resolution is only the right lever if it ACTUALLY reduces something. When the
  // affordable width already meets or exceeds the budget in force, the excess is
  // coming from the number of rows held — usually rows fetched at a wider budget,
  // or full-resolution rows — and narrowing future rows would not recover a byte.
  // Reporting a "reduction" to the same rung would leave the cap permanently
  // exceeded while looking like it had acted.
  if (affordablePerRow >= MIN_RESOLUTION && reduced < currentBudget) {
    return { overCap: true, reducedBudget: reduced, evictRatio: null };
  }

  // Fewer rows is the only lever left. Scale by what is ACTUALLY held rather than
  // by the width we intend future rows to have: the rows already in memory are the
  // ones over the cap, and they do not shrink retroactively.
  const width = Math.max(MIN_RESOLUTION, Math.min(currentBudget, reduced));
  return {
    overCap: true,
    reducedBudget: width,
    evictRatio: maxPoints / currentPoints,
  };
}

/** Retention floor: below this, scrub-back is useless even if memory is tight. */
export const MIN_RETENTION_TICKS = 300;

/** Retention ceiling, so a pathologically narrow row cannot ask for millions. */
export const MAX_RETENTION_TICKS = 20_000;

/**
 * How many ticks a buffer can retain, given how wide its rows actually are.
 *
 * The fixed 600 this replaces was sized for 22,000-point rows at ~176 KB each —
 * the shape telemetry had before liner trim and spatial downsampling. Rows now
 * arrive at 689-2048 points, roughly 5.5-16 KB, so the same heap budget affords
 * an order of magnitude more history. Measured: a 1238-tick cure was truncated
 * to 600, and scrubbing below tick ~638 hit an evicted row.
 *
 * Deriving retention from bytes rather than ticks means it tracks the payload
 * shape automatically — if trim is disabled and rows go back to 22k points, this
 * falls back toward the old figure on its own instead of silently overcommitting.
 */
export function retentionForRowWidth(
  pointsPerRow: number,
  bufferCount: number,
  byteBudget: number
): number {
  const width = pointsPerRow > 0 ? pointsPerRow : 1;
  const buffers = bufferCount > 0 ? bufferCount : 1;
  const ticks = Math.floor(byteBudget / (BYTES_PER_POINT_HEAP * width * buffers));
  return Math.min(MAX_RETENTION_TICKS, Math.max(MIN_RETENTION_TICKS, ticks));
}
